/**
 * Ce qu'on montre d'un exercice : ses deux vues, sa fiche, son exécution.
 *
 * 🔴 **Extrait de `ExercisesSection` parce qu'il a un second consommateur**
 * *(29/08/2026)* : la fiche s'ouvre désormais **depuis la séance en direct**, en
 * touchant le nom ou la vignette d'un exercice. C'est la raison la plus banale
 * du monde de vouloir la voir — « je fais quoi, là, exactement ? » — et
 * l'obliger à sortir de la séance pour aller la chercher dans la bibliothèque
 * était le contraire d'un écran « utilisable d'une main » (specs §12.3).
 *
 * ⚠️ **Extrait plutôt que recopié**, et c'est la même discipline que
 * `shared/search.ts` à l'étape 4 : deux implémentations de la même vue
 * finissent par diverger, et le symptôme serait qu'un exercice se lise
 * autrement selon l'endroit d'où on l'ouvre. Personne ne signale ce
 * défaut-là — on se dit qu'on avait mal vu.
 *
 * ⚠️ **Les instructions se chargent à la CONSULTATION** (§1.1, politique des
 * images appliquée au texte) : elles pèsent 571 Ko sur les 869 Ko du jeu, et la
 * liste est répliquée **et** persistée. Hors ligne, un exercice déjà consulté
 * s'affiche entièrement — sa clé est persistée ; un exercice jamais ouvert
 * montre ce que la liste sait de lui et une note **neutre** pour le reste,
 * jamais un bandeau rouge (règle du §3.5).
 */

import { useQuery } from '@tanstack/react-query';

import type { ExerciseSummaryDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { exerciseImageUrl, exerciseKey, fetchExercise } from '../../api/exercises';
import { exerciseHistoryKey, fetchExerciseHistory } from '../../api/sessions';
import { Card } from '../../components/nourri';
import { ReadNote } from '../../components/ui/ReadNote';
import { ExerciseChart } from './ExerciseChart';
import { NIVEAU_FR } from './exerciseFilters';
import { compactResult } from './setLabels';

export function ExerciseFacts({ exercise }: { exercise: ExerciseSummaryDTO }) {
  const detail = useQuery({
    queryKey: exerciseKey(exercise.clientId),
    queryFn: () => fetchExercise(exercise.clientId),
  });

  const etapes = (detail.data?.instructions ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

  /*
   * ⚠️ Lié à une constante, et pas seulement testé : TypeScript ne conserve pas
   * l'affinement d'une propriété d'objet à l'intérieur d'une fermeture — celle
   * du `map` ci-dessous.
   */
  const mediaKey = exercise.mediaKey;

  return (
    <>
      {/*
        ⚠️ **Les deux vues, pas une seule** (§1.1) : ce sont la position de
        départ et la position finale, et c'est leur juxtaposition qui *est* le
        mouvement. N'en montrer qu'une reviendrait à montrer une pose.
        ⚠️ Un exercice sans image ne réserve aucun espace.
      */}
      {mediaKey !== null && exercise.imageCount > 0 && (
        <div className="n-exo__frames">
          {Array.from({ length: exercise.imageCount }, (_, index) => (
            <img
              key={index}
              className="n-exo__frame"
              src={exerciseImageUrl(mediaKey, index)}
              alt={
                index === 0
                  ? `${exercise.name} — position de départ`
                  : `${exercise.name} — position ${String(index + 1)}`
              }
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
      )}

      <Card>
        <p className="n-list__meta" style={{ margin: 0 }}>
          {[exercise.category, exercise.equipment, NIVEAU_FR[exercise.level] ?? exercise.level]
            .filter((item): item is string => item !== null && item !== '')
            .join(' · ')}
        </p>
        {exercise.primaryMuscles.length > 0 && (
          <p style={{ margin: '0.4rem 0 0' }}>
            <b>Principaux :</b> {exercise.primaryMuscles.join(', ')}
          </p>
        )}
        {exercise.secondaryMuscles.length > 0 && (
          <p className="n-list__meta" style={{ margin: '0.2rem 0 0' }}>
            Secondaires : {exercise.secondaryMuscles.join(', ')}
          </p>
        )}
      </Card>

      <ReadNote error={readError(detail)} quoi="l’exécution de cet exercice" />

      {etapes.length > 0 && (
        <Card>
          <ol className="n-exo__steps">
            {etapes.map((etape, index) => (
              <li key={index}>{etape}</li>
            ))}
          </ol>
        </Card>
      )}

      <ExerciseHistory clientId={exercise.clientId} />
    </>
  );
}

/**
 * L'historique de l'exercice — la charge dans le temps, puis le détail.
 *
 * 🔴 **Demandé après usage** *(29/08/2026)* : « cette fenêtre flottante doit
 * aussi afficher un historique de l'exercice avec un graphique charge/poids
 * dans le temps ». C'est la question qu'on se pose en ouvrant une fiche devant
 * la barre — *est-ce que je monte ?* — et à laquelle ni la routine ni la séance
 * ne répondaient.
 *
 * ⚠️ **Chargé à la consultation**, comme les instructions et les images : on
 * n'ouvre pas trente séances pour huit cent soixante-treize exercices. Une fois
 * lu, il reste disponible hors ligne (sa clé est persistée).
 *
 * ⚠️ **Une note neutre, jamais un bandeau rouge**, quand il n'a pas pu être
 * lu : c'est la règle du §3.5 — la question est « ai-je quelque chose à
 * montrer ? », et un exercice jamais fait n'est pas une erreur.
 */
function ExerciseHistory({ clientId }: { clientId: string }) {
  const history = useQuery({
    queryKey: exerciseHistoryKey(clientId),
    queryFn: () => fetchExerciseHistory(clientId),
  });

  const passages = history.data ?? [];

  return (
    <>
      <ReadNote error={readError(history)} quoi="l’historique de cet exercice" />

      {history.data !== undefined && passages.length === 0 && (
        <p className="n-empty">Jamais fait. Il apparaîtra ici après une première séance.</p>
      )}

      {passages.length > 0 && (
        <Card>
          {/*
            🔴 **Le graphique décide SEUL de ce qu'il peut tracer, et le dit
            lui-même quand il ne peut rien** *(06/09/2026)*.

            Cet écran portait auparavant un pré-test — « au moins deux passages
            avec une charge, ou deux avec un volume » — doublé d'un commentaire
            qui affirmait pourtant que « dupliquer sa règle ferait deux vérités
            sur la même question ». Le commentaire avait raison et le code ne le
            suivait pas : le jour où le graphique a changé de métriques
            (cinq au lieu de deux, et les séries d'échauffement écartées), les
            deux règles se sont mises à répondre différemment — et la carte
            aurait affiché **ni courbe ni explication**.

            ⚠️ Le pré-test n'est donc pas *corrigé*, il est **supprimé** : c'est
            la seule forme qui ne peut pas diverger.
          */}
          <ExerciseChart history={passages} />

          <ul className="n-exo-history">
            {passages.map((passage) => (
              <li key={passage.sessionClientId} className="n-exo-history__row">
                <span className="n-exo-history__date tnum">{passage.date}</span>
                <span className="n-exo-history__sets tnum">
                  {passage.sets.map((set) => compactResult(set)).join('  ·  ')}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
